import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import LoadingSpinner from '../../components/LoadingSpinner';
import TwoFactorSection from '../../components/TwoFactorSection';

import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';

function Profile() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    // User table fields
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    is_active: true,
    role: '',
    
    // UserInfo table fields
    first_name: '',
    last_name: '',
    org: '',
    org_name: '',
    title: '',
    city: '',
    country: '',
    avatar: '',
    office: '',
    mobile: '',
  });
  
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({
    show: false,
    message: '',
    isError: false
  });
  const [countries, setCountries] = useState([]);
  const [visibility, setVisibility] = useState(null);
  
  // Countries data
  const COUNTRIES_DATA = [
    { code: 'AR', name: 'Argentina' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BR', name: 'Brazil' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'CU', name: 'Cuba' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'HN', name: 'Honduras' },
    { code: 'MX', name: 'Mexico' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'PA', name: 'Panama' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'ES', name: 'Spain' }
  ];

  // Get axios config
  const getAxiosConfig = () => ({
    headers: {
      'access_token': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });

  // Fetch user profile data
  useEffect(() => {
    setCountries(COUNTRIES_DATA);
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${Config.APIBaseURL}/user/me`,
        getAxiosConfig()
      );
      
      const user = response.data;
      setUserData(user);
      
      // If we need to show organization name, we already have it in the user data
      let orgName = '';
      if (user.user_info?.org_name) {
        orgName = user.user_info.org_name;
      }
      
      setFormData({
        // User table fields
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        is_active: user.is_active ?? true,
        role: user.role || '',
        
        // UserInfo table fields
        first_name: user.user_info?.first_name || '',
        last_name: user.user_info?.last_name || '',
        org: user.user_info?.org || '',  // Store org ID
        org_name: orgName,  // Store org name for display
        title: user.user_info?.title || '',
        city: user.user_info?.city || '',
        country: user.user_info?.country || '',
        avatar: user.user_info?.avatar || '',
        office: user.user_info?.office || '',
        mobile: user.user_info?.mobile || '',
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setErrors({ general: 'Error loading profile data' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Start loading state
    setIsLoading(true);
    
    // Load the image to be compressed
    const img = new Image();
    const reader = new FileReader();
    
    reader.onload = (event) => {
      img.onload = function() {
        // Create canvas for compression
        const canvas = document.createElement('canvas');
        
        // Calculate new dimensions (maintain aspect ratio)
        let width = img.width;
        let height = img.height;
        const maxDimension = 400; // Max dimension for avatar
        
        if (width > height && width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else if (height > maxDimension) {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with high quality
        let quality = 0.9;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        let iterations = 0;
        
        // Compress iteratively until size is below threshold or max iterations reached
        const compressImage = () => {
          // Calculate estimated size
          const base64Data = dataUrl.split(',')[1];
          const estimatedSize = Math.ceil(base64Data.length * 0.75);
          const maxSize = 250 * 1024; // 250KB
          
          if (estimatedSize <= maxSize || iterations >= 10) {
            // We're done compressing
            setFormData(prev => ({ ...prev, avatar: dataUrl }));
            
            // Clear avatar error if it exists
            if (errors.avatar) {
              setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors.avatar;
                return newErrors;
              });
            }
            
            setIsLoading(false);
            return;
          }
          
          // Continue compressing
          iterations++;
          
          // Reduce quality for next iteration
          quality = Math.max(0.1, quality - 0.1);
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Continue compression on next tick to avoid blocking UI
          setTimeout(compressImage, 0);
        };
        
        // Start compression
        compressImage();
      };
      
      img.onerror = function() {
        setErrors(prev => ({
          ...prev,
          avatar: 'Invalid image file'
        }));
        e.target.value = '';
        setIsLoading(false);
      };
      
      img.src = event.target.result;
    };
    
    reader.onerror = function() {
      setErrors(prev => ({
        ...prev,
        avatar: 'Error reading file'
      }));
      e.target.value = '';
      setIsLoading(false);
    };
    
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    try {
      const userInfoStr = localStorage.getItem('userinfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        // Set the exact visibility value from userinfo data
        setVisibility(Number(userInfo.visibility));
      } else {
        // If no userinfo, default to 1
        setVisibility(1);
      }
    } catch (e) {
      console.error('Error loading visibility setting:', e);
      setVisibility(1); // Default to 1 if there's an error
    }
  }, []);

  const handleVisibilityChange = async (value) => {
    try {
      await axios.put(
        `${Config.APIBaseURL}/user/visibility?visibility=${value}`,
        null,
        getAxiosConfig()
      );

      const userInfoStr = localStorage.getItem('userinfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        userInfo.visibility = value;
        localStorage.setItem('userinfo', JSON.stringify(userInfo));
      }

      setVisibility(value);
    } catch (error) {
      console.error('Failed to update visibility:', error);
      setErrors({ general: 'Failed to update visibility settings.' });
      
      setVisibility(prevVisibility => prevVisibility);
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Username validation
    if (!formData.username) newErrors.username = 'Username is required';
    
    // Email validation
    if (!formData.email) newErrors.email = 'Email is required';
    
    // First/Last name validation
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    
    // Password validation - only check if a new password is being set
    if (formData.password) {
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Match the backend endpoint's expected structure
      const requestData = {
        // User update data
        user: {
          username: formData.username,
          email: formData.email,
        },
        
        // UserInfo update data
        userinfo: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          org: formData.org ? parseInt(formData.org, 10) : null,
          title: formData.title || '',
          city: formData.city || '',
          country: formData.country || '',
          avatar: formData.avatar || '',
          office: formData.office || '',
          mobile: formData.mobile || ''
        }
      };
      
      // Only include password if it's being changed
      if (formData.password) {
        requestData.user.password = formData.password;
      }
      
      // Update the user profile
      const response = await axios.put(
        `${Config.APIBaseURL}/user/profile`,
        requestData,
        getAxiosConfig()
      );
      
      if (response.status === 200) {
        setFeedback({
          show: true,
          message: 'Profile updated successfully',
          isError: false
        });
        
        // Refresh the user data
        fetchUserProfile();
        
        // Hide feedback after 3 seconds
        setTimeout(() => {
          setFeedback({ ...feedback, show: false });
        }, 3000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      
      if (error.response?.data?.detail) {
        setErrors({ 
          general: typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail) 
        });
      } else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Error updating profile. Please try again.' });
      }
      
      setFeedback({
        show: true,
        message: 'Error updating profile',
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/*  Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            
            {/* Page header */}
            <div className="sm:flex sm:justify-between sm:items-center mb-8">
              <div className="mb-4 sm:mb-0">
                {/*<h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">Profile Settings ✨</h1>*/}
              </div>
            </div>

            {/* Profile section */}
            <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm mb-8">
              <div className="flex flex-col md:flex-row md:-mr-px">
                <div className="grow">
                  {/* Form */}
                  <div className="p-6 space-y-6">
                    <h2 className="text-xl text-slate-800 dark:text-slate-100 font-bold mb-5">
                      Mi Perfil
                    </h2>
                    
                    {isLoading && !userData ? (
                      <div className="text-center py-10">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        {/* Feedback message */}
                        {feedback.show && (
                          <div className={`p-4 rounded ${
                            feedback.isError 
                              ? 'bg-rose-500/30 text-rose-400' 
                              : 'bg-emerald-500/30 text-emerald-400'
                          }`}>
                            {feedback.message}
                          </div>
                        )}
                        
                        {/* General error message */}
                        {errors.general && (
                          <div className="mb-4 px-4 py-2 bg-red-100 dark:bg-red-400/10 text-red-600 dark:text-red-400 rounded">
                            {errors.general}
                          </div>
                        )}
                        
                        <form onSubmit={handleSubmit}>
                          {/* Avatar upload */}
                          <div className="mb-6">
                            <div className="flex items-center">
                              {formData.avatar ? (
                                <div className="mr-4">
                                  <img 
                                    src={formData.avatar.startsWith('data:') 
                                      ? formData.avatar 
                                      : `data:image/jpeg;base64,${formData.avatar}`
                                    } 
                                    alt="Avatar preview" 
                                    className="w-20 h-20 rounded-full object-cover" 
                                  />
                                </div>
                              ) : (
                                <div className="mr-4 w-20 h-20 rounded-full bg-slate-600 flex items-center justify-center">
                                  <span className="text-xl text-white font-semibold">
                                    {formData.first_name && formData.last_name ? 
                                      `${formData.first_name[0]}${formData.last_name[0]}` : 
                                      'U'}
                                  </span>
                                </div>
                              )}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="avatar">
                                  Foto de Perfil
                                </label>
                                <input
                                  id="avatar"
                                  type="file"
                                  className="form-input w-full border-slate-200 dark:border-slate-700"
                                  accept="image/*"
                                  onChange={handleAvatarChange}
                                />
                                <div className="text-xs text-slate-400 mt-1">
                                  El tamaño se ajustará automáticamente a 250KB.
                                </div>
                              </div>
                            </div>
                            {typeof visibility === 'number' ? (
                              <div className="pt-6 items-center">
                                <label className="block text-sm font-medium mb-1" htmlFor="username">
                                  Visibilidad <span className="italic text-xs">(controla visibilidad en la mensajería)</span>
                                </label>
                                <div className="relative" onClick={(e) => {
                                  e.preventDefault();
                                  handleVisibilityChange((visibility + 1) % 4);
                                }}>
                                  <div className={`
                                    w-[4.3rem] h-5 rounded-sm transition-colors duration-300 cursor-pointer
                                    ${visibility === 0 ? 'bg-gray-300 dark:bg-gray-600' : 
                                      visibility === 1 ? 'bg-indigo-500 dark:bg-indigo-600' : 
                                      visibility === 2 ? 'bg-indigo-600 dark:bg-indigo-700' : 
                                      'bg-indigo-700 dark:bg-indigo-800'}
                                    relative 
                                  `}>
                                    <div className={`
                                      absolute top-[2px] left-[2px]
                                      h-4 w-4
                                      bg-white rounded-sm
                                      transform transition-transform duration-300 ease-out
                                      ${visibility === 0 ? 'translate-x-0' : 
                                        visibility === 1 ? 'translate-x-[0.95rem]' : 
                                        visibility === 2 ? 'translate-x-[1.95rem]' :
                                        'translate-x-[2.95rem]'}
                                    `}/>
                                  </div>
                                  <span className="text-xs font-medium text-gray-900 dark:text-gray-300">
                                      {visibility === 0 ? 'No visible' : 
                                      visibility === 1 ? 'Solo organización' : 
                                      visibility === 2 ? 'En la industria' :
                                      'Visible para todos'}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                          </div>
                          
                          <div className="space-y-4">
                            {/* Username */}
                            <div>
                              <label className="block text-sm font-medium mb-1" htmlFor="username">
                                Usuario <span className="text-red-500">*</span>
                                {/*Username <span className="text-red-500">*</span>*/}
                              </label>
                              <input
                                id="username"
                                name="username"
                                className={`form-input w-full ${errors.username ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                                type="text"
                                value={formData.username}
                                onChange={handleChange}
                              />
                              {errors.username && (
                                <div className="text-xs mt-1 text-red-500">{errors.username}</div>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* First Name */}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="first_name">
                                  Nombre <span className="text-red-500">*</span>
                                  {/*First Name <span className="text-red-500">*</span>*/}
                                </label>
                                <input
                                  id="first_name"
                                  name="first_name"
                                  className={`form-input w-full ${errors.first_name ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                                  type="text"
                                  value={formData.first_name}
                                  onChange={handleChange}
                                />
                                {errors.first_name && (
                                  <div className="text-xs mt-1 text-red-500">{errors.first_name}</div>
                                )}
                              </div>
                              
                              {/* Last Name */}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="last_name">
                                  Apellido <span className="text-red-500">*</span>
                                  {/*Last Name <span className="text-red-500">*</span>*/}
                                </label>
                                <input
                                  id="last_name"
                                  name="last_name"
                                  className={`form-input w-full ${errors.last_name ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                                  type="text"
                                  value={formData.last_name}
                                  onChange={handleChange}
                                />
                                {errors.last_name && (
                                  <div className="text-xs mt-1 text-red-500">{errors.last_name}</div>
                                )}
                              </div>
                            </div>
                            
                            {/* Email */}
                            <div>
                              <label className="block text-sm font-medium mb-1" htmlFor="email">
                                Email <span className="text-red-500">*</span>
                              </label>
                              <input
                                id="email"
                                name="email"
                                className={`form-input w-full ${errors.email ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                              />
                              {errors.email && (
                                <div className="text-xs mt-1 text-red-500">{errors.email}</div>
                              )}
                            </div>
                            
                            {/* Organization */}
                            <div>
                              <label className="block text-sm font-medium mb-1" htmlFor="org">
                                Organización
                               {/* Organization */}
                              </label>
                              <input
                                id="org"
                                name="org_name"
                                className="form-input w-full border-slate-200 dark:border-slate-700"
                                type="text"
                                value={formData.org_name}
                                readOnly
                                disabled
                              />
                              <div className="text-xs text-slate-400 mt-1">
                                La asignación de la organización puede ser cambiada únicamente por un administrador.
                                {/*Organization assignment can only be changed by an administrator.*/}
                              </div>
                            </div>
                            
                            {/* Title */}
                            <div>
                              <label className="block text-sm font-medium mb-1" htmlFor="title">
                                Cargo
                                {/*Job Title*/}
                              </label>
                              <input
                                id="title"
                                name="title"
                                className="form-input w-full border-slate-200 dark:border-slate-700"
                                type="text"
                                value={formData.title}
                                onChange={handleChange}
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Country */}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="country">
                                  País
                                  {/*Country*/}
                                </label>
                                <select
                                  id="country"
                                  name="country"
                                  className="form-select w-full border-slate-200 dark:border-slate-700"
                                  value={formData.country}
                                  onChange={handleChange}
                                >
                                  <option value="">Select a country</option>
                                  {countries.map(country => (
                                    <option key={country.code} value={country.code}>
                                      {country.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              {/* City */}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="city">
                                  Ciudad
                                  {/*City*/}
                                </label>
                                <input
                                  id="city"
                                  name="city"
                                  className="form-input w-full border-slate-200 dark:border-slate-700"
                                  type="text"
                                  value={formData.city}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Office Phone */}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="office">
                                  Teléfono de Oficina
                                  {/*Office Phone*/}
                                </label>
                                <input
                                  id="office"
                                  name="office"
                                  className="form-input w-full border-slate-200 dark:border-slate-700"
                                  type="text"
                                  value={formData.office}
                                  onChange={handleChange}
                                />
                              </div>
                              
                              {/* Mobile Phone */}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="mobile">
                                  Teléfono Móvil
                                  {/*Mobile Phone*/}
                                </label>
                                <input
                                  id="mobile"
                                  name="mobile"
                                  className="form-input w-full border-slate-200 dark:border-slate-700"
                                  type="text"
                                  value={formData.mobile}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                            
                            {/* Role (read-only) */}
                            <div>
                              <label className="block text-sm font-medium mb-1" htmlFor="role">
                                Rol
                                {/*Role*/}
                              </label>
                              <input
                                id="role"
                                name="role"
                                className="form-input w-full border-slate-200 dark:border-slate-700"
                                type="text"
                                value={formData.role}
                                readOnly
                                disabled
                              />
                              <div className="text-xs text-slate-400 mt-1">
                                {/*Your role can only be changed by an administrator.*/}
                                El rol puede ser cambiado únicamente por un administrador.
                              </div>
                            </div>
                            
                            {/* Change Password section */}
                            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-6">
                              <h3 className="text-base text-slate-800 dark:text-slate-100 font-semibold mb-4">
                                Cambiar Contraseña
                                {/*Change Password*/}
                              </h3>
                              
                              {/* Password */}
                              <div>
                                <label className="block text-sm font-medium mb-1" htmlFor="password">
                                  Nueva Contraseña
                                  {/*New Password*/}
                                </label>
                                <input
                                  id="password"
                                  name="password"
                                  className={`form-input w-full ${errors.password ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                                  type="password"
                                  value={formData.password}
                                  onChange={handleChange}
                                  placeholder="Dejar en blanco para mantener la contraseña actual"
                                  /*placeholder="Leave blank to keep current password"*/
                                />
                                {errors.password && (
                                  <div className="text-xs mt-1 text-red-500">{errors.password}</div>
                                )}
                              </div>
                              
                              {/* Confirm Password */}
                              <div className="mt-3">
                                <label className="block text-sm font-medium mb-1" htmlFor="confirmPassword">
                                  Confirmar Nueva Contraseña
                                  {/*Confirm New Password*/}
                                </label>
                                <input
                                  id="confirmPassword"
                                  name="confirmPassword"
                                  className={`form-input w-full ${errors.confirmPassword ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                                  type="password"
                                  value={formData.confirmPassword}
                                  onChange={handleChange}
                                />
                                {errors.confirmPassword && (
                                  <div className="text-xs mt-1 text-red-500">{errors.confirmPassword}</div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Form buttons */}
                          <div className="flex justify-end mt-6">
                            <button
                              type="submit"
                              className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
                              disabled={isLoading}
                            >
                              {isLoading ? <LoadingSpinner small /> : 'Actualizar Perfil'}
                            </button>
                          </div>
                        </form>

                        {/* Two-Factor Authentication section */}
                        <TwoFactorSection />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Profile; 